import moment from "moment";
import React, { useState } from "react";
import { useEffect, useRef } from "react";
import WeekEvents from "./WeekEvents";
import ShowInfoAfspraak from "./ShowInfoAfspraak";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/20/solid";
import { generateDaysArrayForWeek } from "./calenderUtilties";
import CrudEventCalendly from "./CrudEventCalendly";
import CrudEventCalendlyPatienten from "./CrudEventCalendlyPatienten";

const daysArray = [];
const currentMonth = moment().locale("nl-be").format("YYYY-MM");
const selectedDay = daysArray.find((day) => day.isSelected);

// components/Day.js

const Header = ({ formattedMonth, currentMonth, setOpen }) => (
  <header className="flex items-center justify-between border-b border-wa-border bg-wa-panel px-4 py-3 sm:px-6 lg:flex-none">
    <h1 className="text-lg font-semibold leading-6 text-wa-text">
      <time dateTime={currentMonth}>
        {moment(formattedMonth).locale("nl-be").format("MMMM YYYY")}
      </time>
    </h1>
    <div className="flex items-center">
      <div className="hidden md:ml-4 md:flex md:items-center">
        <div className="ml-6 h-6 w-px bg-wa-border" />
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="ml-6 rounded-md bg-wa-accent px-3 py-2 text-sm font-semibold text-wa-onaccent shadow-sm hover:bg-wa-accentdeep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-wa-accent"
        >
          Afspraak maken
        </button>
      </div>
    </div>
  </header>
);

const HeaderDays = ({ days, handlePreviousWeek, handleNextWeek }) => {
  return (
    <div className="mt-2 hidden h-full grid-cols-7 divide-x divide-wa-border border-r border-wa-border text-sm leading-6 text-wa-muted sm:grid">
      <div className="col-end-1 w-14" />
      {days.map((day, index) => (
        <div
          key={index}
          className="flex gap-x-1 items-center justify-center py-3 "
        >
          {index == 0 && (
            <button
              type="button"
              onClick={handlePreviousWeek}
              className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-wa-muted hover:text-wa-text"
            >
              <span className="sr-only">Previous month</span>
              <ChevronLeftIcon className="h-7 w-7" aria-hidden="true" />
            </button>
          )}
          <span className="items-center justify-center font-semibold text-wa-text">
            {day.titleDay}
          </span>
          <span className="items-center justify-center font-semibold text-wa-text">
            {day.numDay}
          </span>
          {index == 6 && (
            <button
              type="button"
              onClick={handleNextWeek}
              className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-wa-muted hover:text-wa-text"
            >
              <span className="sr-only">Previous month</span>
              <ChevronRightIcon
                className="h-7 w-7"
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

const HoursLines = ({ containerOffset }) => {
  const hours = Array.from({ length: 15 }, (_, i) => {
    const acht = i + 8;
    const hour = Math.floor(acht).toString().padStart(2, "0"); // Get the full hour
    const minutes = "00"; // Alternate between ":00" and ":30"
    return `${hour}:${minutes}`; // Combine hour and minutes
  });

  return (
    <div
      className="col-start-1 col-end-2 row-start-1 grid divide-y divide-wa-border bg-wa-panel2 text-wa-text"
      style={{ gridTemplateRows: "repeat(15, minmax(8rem, 1fr))" }}
    >
      <div
        ref={containerOffset}
        className="row-end-1 h-7 bg-wa-panel"
      ></div>
      {hours.map((hour, index) => (
        <React.Fragment key={index}>
          <div>
            <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-wa-muted">
              <span className="text-sm">{hour}</span>
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

const DaysLines = () => {
  const numColumns = 8; // Change this to dynamically adjust the number of columns

  return (
    <div
      className={`col-start-1 col-end-2 row-start-1 hidden grid-cols-7 grid-rows-1 divide-x divide-wa-border sm:grid sm:grid-cols-7`}
    >
      {Array.from({ length: numColumns }, (_, index) => (
        <div
          key={index}
          className={`col-start-${index + 1} row-span-full ${
            index === numColumns - 1 ? "w-8" : ""
          }`}
        />
      ))}
    </div>
  );
};

const Schedule = ({
  containerNav,
  container,
  containerOffset,
  days,
  showAfspraak,
  handleNextWeek,
  handlePreviousWeek,
}) => (
  <div
    ref={container}
    className="isolate flex flex-auto flex-col overflow-auto bg-wa-panel2"
  >
    <div
      style={{ width: "165%" }}
      className="flex max-w-full flex-none flex-col sm:max-w-none md:max-w-full"
    >
      <div
        ref={containerNav}
        className="sticky top-0 z-30 flex-none bg-wa-panel2 shadow ring-1 ring-wa-border sm:pr-8"
      >
        <HeaderDays
          days={days}
          handleNextWeek={handleNextWeek}
          handlePreviousWeek={handlePreviousWeek}
        />
      </div>
      <div className="flex flex-auto bg-wa-panel2">
        <div className="sticky left-0 z-10 w-14 flex-none bg-wa-panel2 ring-1 ring-wa-border" />
        <div className="grid flex-auto grid-cols-1 grid-rows-1 bg-wa-panel2">
          <HoursLines containerOffset={containerOffset} />
          <DaysLines />
          {days?.length > 0 &&
            days?.map((item) => (
              <WeekEvents showAfspraak={showAfspraak} evenmentes={item?.events} />
            ))}
        </div>
      </div>
    </div>
  </div>
);

const WeekView = ({ userdetails, user, select, appointments }) => {
  const [open, setOpen] = useState(false);
  const [afspraak, setAfspraak] = useState(null);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [currentDate, setCurrentDate] = useState(moment().locale("nl-be")); // Current week starting date
  const [days, setDays] = useState(
    generateDaysArrayForWeek(currentDate, selectedDay, appointments)
  );

  const handlePreviousWeek = () => {
    const newDate = currentDate.clone().subtract(1, "weeks");
    setCurrentDate(newDate); // Update the current week
  };

  // Handle moving to the next week
  const handleNextWeek = () => {
    const newDate = currentDate.clone().add(1, "weeks");
    setCurrentDate(newDate); // Update the current week
  };

  useEffect(() => {
    setDays(generateDaysArrayForWeek(currentDate, selectedDay, appointments));
  }, [currentDate, appointments, selectedDay]);

  const container = useRef(null);
  const containerNav = useRef(null);
  const containerOffset = useRef(null);

  const showAfspraak = (afspr) => {
    setAfspraak(afspr);
    setOpenModalEdit(true);
  };

  useEffect(() => {
    // Set the container scroll position based on the current time.
    const currentMinute = new Date().getHours() * 60;
    container.current.scrollTop =
      ((container.current.scrollHeight -
        containerNav.current.offsetHeight -
        containerOffset.current.offsetHeight) *
        currentMinute) /
      1440;
  }, []);

  return (
    <div className="flex h-full flex-col rounded-lg border border-wa-border bg-wa-panel text-wa-text">
      <Header
        formattedMonth={currentDate}
        setOpen={setOpen}
        currentMonth={currentMonth}
      />
      <Schedule
        container={container}
        containerNav={containerNav}
        containerOffset={containerOffset}
        days={days}
        showAfspraak={showAfspraak}
        appointments={appointments}
        handlePreviousWeek={handlePreviousWeek}
        handleNextWeek={handleNextWeek}
      />
       {user?.role == 2 ? (
        <CrudEventCalendly openModel={open} setOpenModel={setOpen} />
      ) : (
        <CrudEventCalendlyPatienten openModel={open} setOpenModel={setOpen} />
      )}
      {afspraak && (
        <ShowInfoAfspraak
          open={openModalEdit}
          setOpen={setOpenModalEdit}
          select={select}
          evenment={afspraak}
          user={user}
        />
      )}
    </div>
  );
};

export default WeekView;
